import { useState } from "react";
// ui
import { Button, Box, Alert, IconButton, Tooltip, Typography } from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// ★cmd-po_1139: PoModalShell + PoButton で catalog 外殻を導入
import { PoModalShell } from "../common/PoModalShell";
import { PoButton } from "../common/PoButton";

interface ResetPasswordModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  resetPasswordInf: {
    status: null | "loading" | "open" | "success" | "failed";
    key: { [key: string]: string };
    message: string;
  };
  setResetPasswordInf: (value: {
    status: null | "loading" | "open" | "success" | "failed";
    key: { [key: string]: string };
    message: string;
  }) => void;
  resetPassword: () => void;
}

export function ResetPasswordModal(props: ResetPasswordModalProps) {
  const {
    onModal,
    swCloseModal,
    resetPasswordInf,
    setResetPasswordInf,
    resetPassword,
  } = props;

  const [showPassword, setShowPassword] = useState(false);

  const footer =
    resetPasswordInf.status === "open" ? (
      <>
        <PoButton
          variant="secondary"
          onClick={swCloseModal}
          data-testid="reset-password-close-button"
        >
          閉じる
        </PoButton>
        <PoButton
          variant="primary"
          onClick={resetPassword}
          data-testid="reset-password-execute-button"
        >
          パスワードリセット
          <VpnKeyIcon fontSize="small" />
        </PoButton>
      </>
    ) : resetPasswordInf.status === "success" ? (
      <PoButton
        variant="secondary"
        onClick={swCloseModal}
        data-testid="reset-password-success-close-button"
      >
        閉じる
      </PoButton>
    ) : resetPasswordInf.status === "failed" ? (
      <PoButton
        variant="secondary"
        data-testid="reset-password-retry-button"
        onClick={() =>
          setResetPasswordInf({
            ...resetPasswordInf,
            status: "open",
            message: "",
          })
        }
      >
        戻る
      </PoButton>
    ) : null;

  return (
    <PoModalShell
      open={onModal}
      title="パスワードリセット"
      onClose={swCloseModal}
      testidPrefix="reset-password"
      footer={footer}
    >
      {resetPasswordInf.status === "open" ? (
        <Alert
          severity="error"
          sx={{
            bgcolor: "var(--sem-error-bg, #f6d5d1)",
            color: "var(--sem-error, #a53229)",
            fontSize: 12,
            borderRadius: "var(--radius-md, 10px)",
            p: "var(--sp-3, 12px) var(--sp-4, 16px)",
            "& .MuiAlert-icon": { color: "var(--sem-error, #a53229)" },
          }}
        >
          初期パスワードに強制的に変更します。
        </Alert>
      ) : resetPasswordInf.status === "success" ? (
        <>
          {/* ★保全ガード③: 認証情報3点リスト+マスク切替+一括コピー機能は不可触 */}
          <Box sx={{ mb: 2 }}>
            パスワードのリセットに成功しました。
          </Box>
          {[
            { label: "アカウント名", value: resetPasswordInf.key.account_name || "", masked: false },
            { label: "メールアドレス", value: resetPasswordInf.key.email || "", masked: false },
            { label: "初期パスワード", value: resetPasswordInf.key.temp_password || "", masked: true },
          ].map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", mb: 1.5, py: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                  {item.masked && !showPassword ? "●●●●●●●●" : item.value}
                </Typography>
              </Box>
              {item.masked && (
                <Tooltip title={showPassword ? "パスワードを隠す" : "パスワードを表示"}>
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} size="small" sx={{ ml: 0.5 }}>
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            fullWidth
            sx={{ mt: 1, textTransform: "none" }}
            data-testid="reset-password-copy-all"
            onClick={() => {
              const text = `アカウント名: ${resetPasswordInf.key.account_name || ""}\nメールアドレス: ${resetPasswordInf.key.email || ""}\n初期パスワード: ${resetPasswordInf.key.temp_password || ""}`;
              navigator.clipboard.writeText(text);
            }}
          >
            すべてコピー
          </Button>
        </>
      ) : resetPasswordInf.status === "failed" ? (
        <Box className="result-state -failed" data-testid="result-state-failed">
          <span className="rs-title">{resetPasswordInf.message}</span>
        </Box>
      ) : null}
    </PoModalShell>
  );
}
